import Fuse from 'fuse.js';

import { Icon, type IconName } from '@gitbook/icons';
import { type IconDefinition, icons } from '@gitbook/icons/icons';
import { createPalette, type PaletteDef } from '@gitbook/react-uikit';

/**
 * Create a commands palette to pick an icon.
 */
export function createPickIconPalette(
    pick: (code: IconName) => void,
    options: {
        /**
         * Maximum number of icons shown as suggestions
         */
        max?: number;

        /**
         * Palette options.
         */
        withSearch?: boolean;
    } = {}
): PaletteDef {
    const { withSearch = true, max = 5 } = options;

    return createPalette({
        search: withSearch ? { placeholder: 'Select an icon…' } : undefined,
        groups: [
            {
                key: 'icons',
                commands: (query) => {
                    const iconsToDisplay = query ? searchIcons(query) : pickRandomIcons(max);

                    return iconsToDisplay.slice(0, max).map((icon) => {
                        return {
                            key: icon.icon,
                            title: `:${icon.icon}:`,
                            icon: <Icon icon={icon.icon} className="text-muted" />,
                            trigger: () => pick(icon.icon),
                        };
                    });
                },
            },
        ],
    });
}

let fuse: Fuse<IconDefinition> | null = null;
function searchIcons(query: string): IconDefinition[] {
    fuse ??= new Fuse(icons, {
        shouldSort: true,
        threshold: 0.4,
        distance: 100,
        keys: [
            {
                name: 'label',
                weight: 0.7,
            },
            {
                name: 'search',
                weight: 0.3,
            },
        ],
    });

    return fuse.search(query).map((x) => x.item);
}

function pickRandomIcons(count: number): IconDefinition[] {
    const result: IconDefinition[] = [];
    const indices = new Set<number>();

    while (result.length < count) {
        const randomIndex = Math.floor(Math.random() * icons.length);
        if (!indices.has(randomIndex)) {
            indices.add(randomIndex);
            result.push(icons[randomIndex]);
        }
    }

    return result;
}
